import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { MenuIcon, SunIcon, MoonIcon, BellIcon, SearchIcon, UserIcon, LogOutIcon } from 'lucide-react';
import Breadcrumbs from './Breadcrumbs';
interface HeaderProps {
  toggleSidebar: () => void;
}
const Header: React.FC<HeaderProps> = ({
  toggleSidebar
}) => {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const notifications = [{
    id: 1,
    text: 'New merchant onboarding request',
    time: '5 min ago',
    read: false
  }, {
    id: 2,
    text: 'Document review pending for ABC Corp',
    time: '1 hour ago',
    read: false
  }, {
    id: 3,
    text: 'System maintenance scheduled',
    time: '3 hours ago',
    read: true
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
  };
  return <header className="glass-header sticky top-0 z-30 px-4 md:px-6 py-3 transition-all duration-300" data-id="element-363">
      <div className="flex items-center justify-between" data-id="element-364">
        <div className="flex items-center space-x-4" data-id="element-365">
          <button onClick={toggleSidebar} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors" aria-label="Toggle sidebar" data-id="element-366">
            <MenuIcon className="h-5 w-5" data-id="element-367" />
          </button>
          <div className="hidden sm:block" data-id="element-368">
            <Breadcrumbs data-id="element-369" />
          </div>
        </div>

        <div className="hidden md:flex flex-grow max-w-md mx-8" data-id="element-370">
          <form onSubmit={handleSearch} className="relative w-full group" data-id="element-371">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-372">
              <SearchIcon className="h-4 w-4 text-slate-400 group-focus-within:text-accent-500 transition-colors" data-id="element-373" />
            </div>
            <input type="search" className="block w-full py-2 pl-10 pr-4 text-sm rounded-xl bg-slate-100/80 border-transparent text-slate-900 placeholder-slate-400 focus:bg-white focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 dark:bg-slate-800/80 dark:text-white dark:placeholder-slate-500 dark:focus:bg-slate-800 transition-all duration-200" placeholder="Search merchants, transactions, IDs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-374" />
          </form>
        </div>

        <div className="flex items-center space-x-2 md:space-x-4" data-id="element-375">
          <button onClick={toggleTheme} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors" aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-376">
            {theme === 'dark' ? <SunIcon className="h-5 w-5" data-id="element-377" /> : <MoonIcon className="h-5 w-5" data-id="element-378" />}
          </button>

          <div className="relative" data-id="element-379">
            <button onClick={() => {
            setShowNotifications(!showNotifications);
            setShowUserMenu(false);
          }} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors relative" aria-label="Notifications" data-id="element-380">
              <BellIcon className="h-5 w-5" data-id="element-381" />
              <span className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-900" data-id="element-382"></span>
            </button>

            {showNotifications && <div className="absolute right-0 mt-3 w-80 rounded-xl shadow-xl bg-white border border-slate-100 dark:bg-slate-800 dark:border-slate-700 overflow-hidden z-50 transform origin-top-right transition-all" data-id="element-383">
                <div className="py-3 px-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex justify-between items-center" data-id="element-384">
                  <span className="font-semibold text-sm text-slate-900 dark:text-white" data-id="element-385">
                    Notifications
                  </span>
                  <span className="text-xs font-medium bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400 px-2 py-0.5 rounded-full" data-id="element-386">
                    2 New
                  </span>
                </div>
                <div className="max-h-96 overflow-y-auto" data-id="element-387">
                  {notifications.map(notification => <div key={notification.id} className={`px-4 py-3 border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer ${notification.read ? 'opacity-70' : 'bg-accent-50/30 dark:bg-accent-900/10'}`} data-id="element-388">
                      <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug" data-id="element-389">
                        {notification.text}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 font-medium" data-id="element-390">
                        {notification.time}
                      </p>
                    </div>)}
                </div>
                <div className="py-3 px-4 text-center text-sm font-medium text-accent-600 dark:text-accent-400 hover:bg-slate-50 dark:hover:bg-slate-700/30 cursor-pointer transition-colors" data-id="element-391">
                  View all notifications
                </div>
              </div>}
          </div>

          <div className="pl-2 md:pl-4 border-l border-slate-200 dark:border-slate-700 relative" data-id="element-392">
            <button className="flex items-center space-x-3 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" onClick={() => {
            setShowUserMenu(!showUserMenu);
            setShowNotifications(false);
          }} data-id="element-393">
              <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-accent-500 to-violet-500 flex items-center justify-center text-white ring-2 ring-white dark:ring-slate-900 shadow-sm" data-id="element-394">
                <span className="text-sm font-semibold" data-id="element-395">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              {user && <div className="hidden md:flex flex-col items-start" data-id="element-396">
                  <span className="text-sm font-semibold text-slate-900 dark:text-white leading-none" data-id="element-397">
                    {user.name}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-none" data-id="element-398">
                    {user.role}
                  </span>
                </div>}
            </button>

            {showUserMenu && <div className="absolute right-0 mt-3 w-56 rounded-xl shadow-xl bg-white border border-slate-100 dark:bg-slate-800 dark:border-slate-700 overflow-hidden z-50" data-id="element-399">
                <div className="py-2" data-id="element-400">
                  <div className="px-4 py-2 md:hidden border-b border-slate-100 dark:border-slate-700 mb-2" data-id="element-401">
                    <span className="block text-sm font-semibold text-slate-900 dark:text-white" data-id="element-402">
                      {user?.name}
                    </span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-id="element-403">
                      {user?.role}
                    </span>
                  </div>
                  <button className="flex items-center w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors" data-id="element-404">
                    <UserIcon className="h-4 w-4 mr-3 text-slate-400" data-id="element-405" />
                    Your Profile
                  </button>
                  <button className="flex items-center w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors" data-id="element-406">
                    <SearchIcon className="h-4 w-4 mr-3 text-slate-400" data-id="element-407" />
                    Settings
                  </button>
                  <div className="h-px bg-slate-100 dark:bg-slate-700 my-2" data-id="element-408"></div>
                  <button className="flex items-center w-full text-left px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" onClick={() => {
                logout();
                setShowUserMenu(false);
              }} data-id="element-409">
                    <LogOutIcon className="h-4 w-4 mr-3" data-id="element-410" />
                    Sign out
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Mobile Search - Only visible on small screens */}
      <div className="md:hidden mt-3" data-id="element-411">
        <form onSubmit={handleSearch} className="relative w-full group" data-id="element-412">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-413">
            <SearchIcon className="h-4 w-4 text-slate-400 group-focus-within:text-accent-500 transition-colors" data-id="element-414" />
          </div>
          <input type="search" className="block w-full py-2 pl-10 pr-4 text-sm rounded-xl bg-slate-100/80 border-transparent text-slate-900 placeholder-slate-400 focus:bg-white focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 dark:bg-slate-800/80 dark:text-white dark:placeholder-slate-500 dark:focus:bg-slate-800 transition-all duration-200" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-415" />
        </form>
      </div>
    </header>;
};
export default Header;